<template>
  <div class="benchmarks-card-content">
    <div class="title-content">
      <BenchmarksCardTitle :title="titleFull"/>
      <Legend
        :labels="legendLabels"
        :dotColors="legendDotColors"
      />
    </div>
    <div v-if="loading" class="loading-content">
      <Loader
        :loading="loading"
      />
    </div>
    <div v-else class="data-content">
      <BenchmarksChart
        :data="filteredData"
        :width="chartWidth"
        :height="chartHeight"
        :theme="theme"
        :dimension="dimension"
        :isInPercentages="isInPercentages"
        :useShortLabel="useShortLabel"
        :labels="labels"
      >
      </BenchmarksChart>
      <div class="bottom-container">
        <div v-if="showBrush">
          <div class="brush-container">
            <BenchmarksChartBrush
              v-if="showBrush"
              :data="data"
              :displayAmount="displayLimit"
              :width="brushWidth"
              @select="handleSelection"
            >
            </BenchmarksChartBrush>
          </div>
          <div class="benchmarks-brush-text">
            DRAG PREVIEW TO VIEW MORE
          </div>
        </div>
        <div class="data-comparison-container">
          <DataComparison
            :title="titleShort"
            :primaryData="formattedData.primary"
            :primaryLabel="this.labels['primary']"
            :secondaryData="formattedData.secondary"
            :secondaryLabel="this.labels['secondary']"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BenchmarksChart from '../../../Benchmarks/BenchmarksChart/BenchmarksChart.vue';
import BenchmarksChartBrush
  from '../../../Benchmarks/BenchmarksChart/BenchmarksChartBrush.vue';
import BenchmarksCardTitle from '../BenchmarksCardTitle/BenchmarksCardTitle.vue';
import Legend from '../../../../common/Legend.vue';
import DataComparison from '../../../Benchmarks/DataComparison/DataComparison.vue';
import Loader from '../../../Common/Charting/Shared/Loader.vue';
import utils from '../../../../../services/utils';
import { mapState, mapGetters } from 'vuex';

const defaultChartWidth = 1280;
const defaultChartHeight = 284;

export default {
  props: {
    theme: {
      type: String,
      default: 'category',
      validator: v => {
        return ['category', 'brand'].indexOf(v) > -1;
      },
    },
    data: {
      type: Array,
      required: true,
    },
    summary: {
      type: Object,
      required: true,
    },
    labels: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    dimension: {
      type: String,
    },
    isInPercentages: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    legendLabels: {
      type: Array,
      required: true,
    },
    legendDotColors: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dataWindow: [],
      chartWidth: defaultChartWidth,
      chartHeight: defaultChartHeight,
    };
  },
  components: {
    BenchmarksChart,
    BenchmarksChartBrush,
    BenchmarksCardTitle,
    Legend,
    DataComparison,
    Loader,
  },
  methods: {
    handleSelection(val) {
      this.dataWindow = val;
    },
    handleResize() {
      const { innerWidth } = window;

      this.chartWidth = innerWidth > 1562
        ? defaultChartWidth
        : innerWidth - 282;
    },
  },
  computed: {
    ...mapState({
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
      selectedSubcategories: state => state.dashboard.selectedSubcategories,
    }),
    ...mapGetters([
      'benchmarksChartKeys',
    ]),
    showBrush() {
      return this.data.length > this.displayLimit;
    },
    displayLimit() {
      const numberOfBars = this.benchmarksChartKeys.length;

      switch (numberOfBars) {
        case 3:
          return 8;
        case 4:
          return 7;
        case 5:
          return 6;
        default:
          return 11;
      }
    },
    filteredData() {
      return this.showBrush
        ? this.data.slice(this.dataWindow[0], this.dataWindow[1])
        : this.data;
    },
    categoryTitleContent() {
      return this.title
        + ': Your Brand vs '
        + this.formattedCategoryName
        + ' Category'
        + this.subcategoryTitleContent;
    },
    subcategoryTitleContent() {
      const subcategoryCount = this.selectedSubcategories.length;

      if (!subcategoryCount) return '';

      if (subcategoryCount === 1) {
        return ' and 1 Subcategory';
      }

      return ` and ${subcategoryCount} Subcategories`;
    },
    titleFull() {
      return this.theme === 'brand'
        ? this.title
        : this.categoryTitleContent;
    },
    titleShort() {
      return 'Total ' + this.title;
    },
    formattedCategory() {
      return `ALL ${this.category}`;
    },
    formattedCategoryName() {
      const name = this.labels['secondary'] || '';

      return utils.stringToTitleCase(name);
    },
    formattedData() {
      const { base, comparison, category } = this.summary;
      let primary = `${base.toFixed(1)}`;
      let secondary = this.activeBenchmarksView === 'category'
        ? `${category.toFixed(1)}`
        : `${comparison.toFixed(1)}`;

      if (this.isInPercentages) {
        primary += '%';
        secondary += '%';
      }

      return {
        primary: primary,
        secondary: secondary,
      };
    },
    useShortLabel() {
      return this.filteredData.length > 7;
    },
    brushWidth() {
      const numberOfBars = this.benchmarksChartKeys.length;

      switch (numberOfBars) {
        case 4:
          return 575;
        case 5:
          return 675;
        default:
          return 475;
      }
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.handleResize();
  },
};
</script>

<style scoped>
.benchmarks-card-content {
  padding: 16px;
  height: 462px;
}

.brush-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-left: 40px;
  margin-top: 8px;
}

.benchmarks-brush-text {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-family: Lato;
  font-size: 12px;
  font-weight: 500;
  color: #778699;
  margin-left: 48px;
  margin-bottom: 6px;
  margin-top: 8px;
}

.title-content {
  display: flex;
  height: 38px;
  justify-content: space-between;
}

.data-content {
  position: relative;
  height: 419px;
}

.loading-content {
  height: 419px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.data-comparison-container {
  position: absolute;
  bottom: 8px;
  right: 0px;
}
</style>
